import React from 'react'
import {
  ClusterOutlined,
  PlusCircleOutlined,
  SyncOutlined,
  EnterOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import { Link } from '@mui/material'
import { useLocation } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'

import usePortfolios from 'hooks/models/usePortfolios'
// import useTransactions from 'hooks/models/useTransactions'
import useAccounts from 'hooks/models/useAccounts'
import useBalances from 'hooks/models/useBalances'
// import useAuth from 'hooks/useAuth'
import usePermissions from 'hooks/usePermissions'
import { PAGE_TITLES } from 'consts/pageTitles'
import useLinks from 'hooks/models/useLinks'
import useSettlement from 'hooks/models/useSettlement'
import SettlementEmptyStateDescription from 'components/cards/settlement/SettlementEmptyStateDescription'
import { providers, permissionsByName } from 'merklebase-utils/commons'

// ==============================|| PERMISSION ROUTE MAPPING ||============================== //

const PERMISSION_ROUTE_MAP = {
  [permissionsByName.venue_data.id]: [
    '/balances/balances',
    '/balances/positions',
    '/balances/margins',
  ],
  [permissionsByName.loans.id]: ['/lms/credit-agreements', '/lms/tranches'],
  [permissionsByName.defi.id]: [
    '/defi/balances',
    '/defi/subscription-redemption',
    '/defi/performance',
  ],
  [permissionsByName.operations.id]: [
    '/operations/transactions',
    '/reports-new',
    '/reports',
    '/settings/historicalaum',
    '/balances/computed-balances',
  ],
  [permissionsByName.positions.id]: [
    '/risk/risk',
    '/risk/stress-test',
    '/performance/pnl-breakdown',
    '/performance/history-pnl',
  ],
}

const ADMIN_ONLY_ROUTES = [
  '/settings/user-permissions',
  '/settings/user-permissions/roles',
  '/settings/user-permissions/users',
  '/settings/configurations',
  '/settings/liquidity-tiers',
  '/settings/link-management',
  '/tasks/configurations/alerts',
]

// ==============================|| ROUTE EMPTY STATE CONFIGURATIONS ||============================== //

export const routeEmptyStateConfig = {
  conditions: [
    {
      id: 'insufficient-permissions',
      useDataHook: () => {
        const location = useLocation()
        const { hasPermissionAccess, isAdminOrOwner, isRolesFeatureEnabled } = usePermissions()

        const permissionId =
          Object.keys(PERMISSION_ROUTE_MAP).find((id) =>
            PERMISSION_ROUTE_MAP[id].includes(location.pathname)
          ) || null

        const isAdminRoute = ADMIN_ONLY_ROUTES.some(
          (adminRoute) =>
            location.pathname === adminRoute || location.pathname.startsWith(adminRoute + '/')
        )

        let hasAccess = true
        if (permissionId) {
          hasAccess = hasPermissionAccess(permissionId)
        } else if (isAdminRoute) {
          hasAccess = isAdminOrOwner()
        }

        return {
          data: {
            currentRoute: location.pathname,
            permissionId,
            isAdminRoute,
            hasAccess,
            isRolesFeatureEnabled,
          },
          isLoading: false,
        }
      },
      check: (data) => {
        const { permissionId, isAdminRoute, hasAccess, isRolesFeatureEnabled } = data

        if (!isRolesFeatureEnabled) {
          return false
        }

        if (!permissionId && !isAdminRoute) {
          return false
        }

        return !hasAccess
      },
      routes: [...Object.values(PERMISSION_ROUTE_MAP).flat(), ...ADMIN_ONLY_ROUTES],
      useDescriptionHook: () => {
        const location = useLocation()
        return (
          <>
            Access to{' '}
            <FormattedMessage id={PAGE_TITLES[location.pathname]} defaultMessage="this page" />{' '}
            requires permission
            <br />
            Contact an administrator for access
            <br />
            <br />
            If the issue persists, please contact support
          </>
        )
      },
      emptyStateProps: {
        icon: <InfoCircleOutlined />,
        heading: 'Access Restricted',
        sx: {
          height: 'calc(100vh - 88px)',
        },
      },
    },
    {
      id: 'no-accounts',
      useDataHook: () => {
        const { accounts } = useAccounts()
        return {
          data: accounts.data || [],
          isLoading: accounts.isLoading,
        }
      },
      check: (data, isLoading) => {
        return data.length === 0 && !isLoading
      },
      routes: [
        '/dashboard',
        '/balances/balances',
        '/balances/positions',
        '/balances/margins',
        '/balances/computed-balances',
        '/defi/subscription-redemption',
        '/defi/performance',
        '/risk/risk',
        '/risk/stress-test',
        '/operations/transactions',
        '/lms/credit-agreements',
        '/lms/tranches',
        '/performance/pnl-breakdown',
        '/performance/history-pnl',
        '/operations/reconciliation',
        '/settings/portfolios',
      ],
      useHeadingHook: () => {
        const location = useLocation()
        return (
          <>
            Access{' '}
            <FormattedMessage id={PAGE_TITLES[location.pathname]} defaultMessage="Dashboard" />
          </>
        )
      },
      emptyStateProps: {
        icon: <PlusCircleOutlined />,
        heading: 'Access Dashboard',
        description: (
          <>
            Setup accounts in the <Link href="/settings/link-management">Link Management</Link> page
            <br />
            Contact us for assistance
          </>
        ),
        sx: {
          height: 'calc(100vh - 88px)',
        },
      },
    },
    {
      id: 'no-balances-links',
      useDataHook: () => {
        const { links } = useLinks()
        return {
          data: links.data || [],
          isLoading: links.isLoading,
        }
      },
      check: (data, isLoading) => {
        return (
          data.filter((a) =>
            Object.values(providers)
              .filter((a) => a.type === 'defi')
              .map((a) => a.ticker)
              .includes(a.providerTicker)
          ).length === 0 && !isLoading
        )
      },
      routes: ['/defi/balances'],
      emptyStateProps: {
        icon: <PlusCircleOutlined />,
        heading: 'Access DeFi Balances',
        description: (
          <>
            Setup DeFi Wallets in the <Link href="/settings/link-management">Link Management</Link>{' '}
            page
            <br />
            <br />
            Contact us for assistance
          </>
        ),
        sx: {
          height: 'calc(100vh - 88px)',
        },
      },
    },
    {
      id: 'no-portfolios',
      useDataHook: () => {
        const { portfolios } = usePortfolios()
        return {
          data: portfolios.data || [],
          isLoading: portfolios.isLoading,
        }
      },
      check: (data, isLoading) => {
        return data.length === 0 && !isLoading
      },
      routes: [
        '/dashboard',
        '/balances/balances',
        '/balances/positions',
        '/balances/margins',
        '/defi/subscription-redemption',
        '/defi/performance',
        '/risk/risk',
        '/risk/stress-test',
        '/operations/transactions',
        '/performance/pnl-breakdown',
        '/performance/history-pnl',
        '/operations/reconciliation',
      ],
      emptyStateProps: {
        icon: <PlusCircleOutlined />,
        heading: 'Create a portfolio to get started',
        description: (
          <>
            Create a portfolio and map linked accounts in the{' '}
            <Link href="/settings/portfolios">Portfolios</Link> page.
            <br />
            Contact us for assistance
          </>
        ),
        sx: {
          height: 'calc(100vh - 88px)',
        },
      },
    },
    {
      id: 'no-balances-portfolio',
      useDataHook: () => {
        const { portfolios } = usePortfolios()
        return {
          data: portfolios.data || [],
          isLoading: portfolios.isLoading,
        }
      },
      check: (data, isLoading) => {
        return data.length === 0 && !isLoading
      },
      routes: ['/defi/balances'],
      emptyStateProps: {
        icon: <ClusterOutlined />,
        heading: 'Create a portfolio to get started',
        description: (
          <>
            Create a portfolio and map linked DeFi wallets in the{' '}
            <Link href="/settings/portfolios">Portfolios</Link> page
            <br />
            Contact us for assistance
          </>
        ),
        sx: {
          height: 'calc(100vh - 88px)',
        },
      },
    },
    {
      id: 'no-accounts-mapped-to-portfolio',
      useDataHook: () => {
        const { selectedPortfolios } = usePortfolios()
        return {
          data: selectedPortfolios.data || [],
          isLoading: selectedPortfolios.isLoading,
        }
      },
      check: (data, isLoading) => {
        return data.map((a) => a.accountsIds).flat().length === 0 && !isLoading
      },
      routes: [
        '/dashboard',
        '/balances/balances',
        '/balances/positions',
        '/balances/margins',
        '/balances/computed-balances',
        '/defi/subscription-redemption',
        '/defi/performance',
        '/risk/risk',
        '/risk/stress-test',
        '/operations/transactions',
        '/lms/credit-agreements',
        '/lms/tranches',
        '/defi/balances',
        '/performance/pnl-breakdown',
      ],
      emptyStateProps: {
        icon: <ExclamationCircleOutlined />,
        heading: 'Accounts Not Mapped To This Portfolio',
        description: (
          <>
            Visit <Link href="/settings/portfolios">Portfolios</Link> page and map accounts to this
            portfolio,
            <br />
            or select a different portfolio using the filter
            <br />
            <br />
            Contact us for assistance
          </>
        ),
        sx: {
          height: 'calc(100vh - 88px)',
        },
      },
    },
    {
      id: 'no-portfolio-selected',
      useDataHook: () => {
        const { selectedPortfolios } = usePortfolios()
        return {
          data: selectedPortfolios.data || [],
          isLoading: selectedPortfolios.isLoading,
        }
      },
      check: (data, isLoading) => {
        return data.length === 0 && !isLoading
      },
      routes: [
        '/dashboard',
        '/balances/balances',
        '/balances/positions',
        '/balances/margins',
        '/balances/computed-balances',
        '/defi/subscription-redemption',
        '/defi/performance',
        '/risk/risk',
        '/risk/stress-test',
        '/operations/transactions',
        '/lms/credit-agreements',
        '/lms/tranches',
        '/defi/balances',
        '/settings/historicalaum',
        '/performance/history-pnl',
      ],
      emptyStateProps: {
        icon: <EnterOutlined style={{ transform: 'rotate(180deg)' }} />,
        heading: 'No Selected Portfolio',
        description: <>Use the top-right filter to view data by portfolio/s across the system</>,
        sx: {
          height: 'calc(100vh - 88px)',
        },
      },
    },
    {
      id: 'no-balances-recorded',
      useDataHook: () => {
        const { balances } = useBalances()
        return {
          data: balances.data || [],
          isLoading: balances.isLoading,
        }
      },
      check: (data, isLoading) => {
        return data.length === 0 && !isLoading
      },
      routes: ['/balances/balances'],
      emptyStateProps: {
        icon: <SyncOutlined />,
        heading: 'No Balances Recorded',
        description: (
          <>
            Try <Link href="/settings/link-management">refreshing</Link> the data to ensure data is
            up to date
            <br />
            If the issue persists, contact us for assistance
          </>
        ),
        sx: {
          height: 'calc(100vh - 88px)',
        },
      },
    },
    {
      id: 'no-margin-data-found',
      useDataHook: () => {
        const { marginBalances } = useBalances()
        return {
          data: marginBalances.data || [],
          isLoading: marginBalances.isLoading,
        }
      },
      check: (data, isLoading) => {
        return data.length === 0 && !isLoading
      },
      routes: ['/balances/margins'],
      emptyStateProps: {
        icon: <SyncOutlined />,
        heading: 'No Margin Data Found',
        description: (
          <>
            <Link href="/settings/link-management">Refresh</Link> the data here to load the latest
            margin information
            <br />
            If the issue persists, contact us for assistance
          </>
        ),
        sx: {
          height: 'calc(100vh - 88px)',
        },
      },
    },
    // {
    //   id: 'no-transactions-recorded',
    //   useDataHook: () => {
    //     const filter = useMemo(() => ({}), [])
    //     const { transactions } = useTransactions({ filter })
    //     return {
    //       data: transactions.data || [],
    //       isLoading: transactions.isLoading,
    //     }
    //   },
    //   // TODO: Temporarily disabled due to slow API performance
    //   // Re-enable once API performance is improved
    //   check: (data, isLoading) => {
    //     // Temporarily always return false to prevent empty state during slow API loads
    //     return false
    //     // return data.length === 0 && !isLoading
    //   },
    //   routes: [
    //     '/dashboard',
    //     '/operations/reconciliation',
    //     '/risk/risk',
    //     '/risk/stress-test',
    //     '/operations/transactions',
    //     '/performance/pnl-breakdown',
    //   ],
    //   emptyStateProps: {
    //     icon: <SyncOutlined />,
    //     heading: 'No Transactions Recorded Yet',
    //     description: (
    //       <>
    //         Try <Link href="/settings/link-management">refreshing</Link> the data to ensure data is
    //         up to date
    //         <br />
    //         If the issue persists, contact us for assistance
    //       </>
    //     ),
    //     sx: {
    //       height: 'calc(100vh - 88px)',
    //     },
    //   },
    // },
    // {
    //   id: 'no-transactions-booked',
    //   useDataHook: () => {
    //     const filter = useMemo(() => ({}), [])
    //     const { transactions } = useTransactions({ filter })
    //     const { isBookingOrganization } = useAuth()
    //     return {
    //       data: transactions.data || [],
    //       isLoading: transactions.isLoading,
    //       isBookingOrganization,
    //     }
    //   },
    //   // TODO: Temporarily disabled due to slow API performance
    //   // Re-enable once API performance is improved
    //   check: (data, isLoading, isBookingOrganization) => {
    //     // Temporarily always return false to prevent empty state during slow API loads
    //     return false
    //     // return data.length === 0 && !isLoading && !isBookingOrganization
    //   },
    //   routes: ['/dashboard', '/operations/reconciliation', '/risk/risk', '/risk/stress-test'],
    //   emptyStateProps: {
    //     icon: <PlusCircleOutlined />,
    //     heading: 'No Transaction Data Found',
    //     description: (
    //       <>
    //         This page shows reconciled balances and transactions, but none are currently available
    //         <br />
    //         Go to <Link href="/operations/transactions">Transactions</Link> page to book them
    //         manually
    //         <br />
    //         <br />
    //         If the issue persists, contact us for assistance
    //       </>
    //     ),
    //     sx: {
    //       height: 'calc(100vh - 88px)',
    //     },
    //   },
    // },
    {
      id: 'no-counterparties',
      useDataHook: () => {
        const { counterparties } = useSettlement()
        return {
          data: counterparties.data || [],
          isLoading: counterparties.isLoading,
        }
      },
      check: (data, isLoading) => {
        return Array.isArray(data) && data.length === 0 && !isLoading
      },
      routes: [
        '/operations/settlement',
        '/operations/settlement/:portfolioId',
        '/operations/settlement/:portfolioId/policy',
      ],
      emptyStateProps: {
        icon: <SettingOutlined />,
        heading: 'Set Up External Portfolios for Settlement',
        description: <SettlementEmptyStateDescription />,
        sx: {
          height: 'calc(100vh - 88px)',
        },
      },
    },
  ],
}
